import type { Styles } from '@adornis/ass/style.js';
import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { EntityStore } from '@adornis/baseql/client/entityStore.js';
import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import { Einzelberatung } from '@adornis/buchungsheld-db/entities/einzelberatung.js';
import type { Product } from '@adornis/buchungsheld-db/entities/products.js';
import { ParticipationState } from '@adornis/buchungsheld-db/enums/enums.js';
import {
  dataviewEmptyState,
  dataviewLoadingState,
  getParticipationStateColors,
} from '@adornis/buchungsheld-db/global-helpers.js';
import { getPluginForScope } from '@adornis/buchungsheld-db/plugins/plugin.js';

import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import type { NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { BHDialogFreeFormMails } from '@adornis/bh-mails/client/bh-dialog-free-form-mails.js';
import { sendFreeFormMail } from '@adornis/buchungsheld-db/api/users.js';
import { BHFilter } from '@adornis/buchungsheld-db/entities/bh-filter.js';
import { InstitutionMailTemplate, MailTemplateType } from '@adornis/buchungsheld-db/entities/mail-templates.js';
import { UserStammdaten } from '@adornis/buchungsheld-db/entities/stammdaten.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { Permission } from '@adornis/buchungsheld-db/enums/roles.js';
import { type BHTranslationDictionary } from '@adornis/buchungsheld-db/translations/translation.js';
import {
  getEmptyParticipationStateStats,
  ParticipationDataview,
  ParticipationDataviewStateStat,
  subscribeParticipationsStatsByProductID,
  type ParticipationStateStatsMappedNumber,
} from '@adornis/buchungsheld-db/use-case-entities/ParticipationsDataview.js';
import { isDefined } from '@adornis/buchungsheld-db/util.js';
import '@adornis/buchungsheld-theme/components/tr-button-small.js';
import '@adornis/buchungsheld-theme/components/tr-checkbox.js';
import '@adornis/buchungsheld-theme/components/tr-chip.js';
import '@adornis/buchungsheld-theme/components/tr-data-view.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import '@adornis/buchungsheld-theme/components/tr-icon.js';
import '@adornis/buchungsheld-theme/components/tr-tooltip.js';
import '@adornis/buchungsheld-theme/fonts/fonts.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-card.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-icon.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import type { ColorType } from '@adornis/chemistry/elements/theming/coloring-system.js';
import { buildFullOptions, type DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { View } from '@adornis/data-view/db/view.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import '@adornis/dragdrop/x-drag-drop.js';
import { StringAspectConfig } from '@adornis/filter/aspect-schemas/StringAspect.js';
import '@adornis/filter/client/x-filter-editor.js';
import '@adornis/popover/x-dropdown.js';
import type { XDropdown } from '@adornis/popover/x-dropdown.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { guard } from 'lit/directives/guard.js';
import { createRef, ref, type Ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';
import { BehaviorSubject, combineLatest, map, of, switchMap } from 'rxjs';
import { updateParticipationsStateAsAdminUser } from '../../api/participation.js';
import './bh-dropdown-action-content.js';

const defaultExtraColumns = ['buyer.firstName', 'buyer.lastName', 'buyer.email'];

const getLsKey = (product: NarrowedEntity<Product, { _id: 1; _class: 1; institutionID: 1 }>) =>
  `extra_columns:${product?.institutionID ?? 'default'}:${product._class}:v2`;

function getCachedExtraColumns(product: NarrowedEntity<Product, { _id: 1; _class: 1; institutionID: 1 }>) {
  const lsKey = getLsKey(product);
  const extraColumns = localStorage.getItem(lsKey);
  if (extraColumns === null) return null;
  return JSON.parse(extraColumns);
}

function setCachedExtraColumns(
  product: NarrowedEntity<Product, { _id: 1; _class: 1; institutionID: 1 }>,
  columns: string[],
) {
  const lsKey = getLsKey(product);
  localStorage.setItem(lsKey, JSON.stringify(columns));
}

function getViewFor(
  product: NarrowedEntity<Product, { _id: 1; _class: 1; institutionID: 1 }>,
  targetState: ParticipationState,
  selections?: string[],
) {
  return new View({
    filter: new BHFilter({
      searchFields: ['buyer.email', 'buyer.firstName', 'buyer.lastName'],
      sortingFields: ['buyer.lastName'],
      productID: product?._id,
      aspects: [
        // @ts-expect-error this is generally abstract but won't be when we use it
        new (StringAspectConfig('equals').aspectConstructor)({
          fieldName: 'participation.state',
          value: targetState,
        }),
      ],
    }),
    selectedItems: selections ?? [],
  });
}

const emptyUserSelection = Object.values(ParticipationState)
  .filter(it => isNaN(Number(it)))
  .reduce((acc, cur) => ((acc[cur] = []), acc), {}) as Record<ParticipationState, string[]>;
/**
 * @element bh-participation-dataview
 */
@customElement('bh-participation-dataview')
export class BHParticipationDataview extends BuchungsheldLitElement {
  @property({ attribute: false }) product = new RXController<Maybe<Product>>(this, null);
  @property({ attribute: 'mode' }) mode: 'lecturer' | undefined = undefined;

  @state() private readonly _stateStats = new RXController(
    this,
    this.product.observable.pipe(
      switchMap(prod =>
        prod
          ? subscribeParticipationsStatsByProductID(prod._id)(ParticipationDataviewStateStat.neededGqlFields)
          : of(undefined),
      ),
    ),
    map(stats => {
      let fullStats = stats ?? Object.values(getEmptyParticipationStateStats());

      const numMappedByState = Object.fromEntries(
        fullStats.map(stat => [stat.key as ParticipationState, stat.ammount]),
      ) as ParticipationStateStatsMappedNumber;
      return numMappedByState;
    }),
  );

  private _selectedUsers = new RXController(this, emptyUserSelection);

  updateViews = new BehaviorSubject<void>(void 0);
  @state() private readonly _stateViews = new RXController(
    this,
    combineLatest([this.product.observable, this.updateViews]).pipe(
      map(([product]) =>
        Object.fromEntries(
          Object.entries(ParticipationState).map(([k, curStatus]) => [curStatus, getViewFor(product, curStatus)]),
        ),
      ),
    ),
  );

  @state() private _showTableDropAreas = new RXController(this, false);

  @state() private _selectedUserData = new RXController(
    this,
    this._selectedUsers.observable.pipe(
      map(record => Object.values(record).flatMap(x => x)),
      map(selIDs => selIDs.map(id => EntityStore.get(ParticipationDataview._class, id) as ParticipationDataview)),
    ),
  );

  @state() private readonly _currentUser = new RXController(this, currentUser<User>());

  private _selectedMoveDropdown = createRef<XDropdown>();
  private _selectedActionDropdown = createRef<XDropdown>();

  _renderColHeader(icon: string, text: string, styles: Styles = {}) {
    return html`
      <tr-flex w100 center crossaxis-center ${css({ height: this.spacing.lg })}>
        <tr-tooltip placement="bottom" .text=${text}></tr-tooltip>

        ${icon
          ? html` <x-icon type="material-outlined" ${css(styles)}> ${icon} </x-icon> `
          : html` <tr-text ${css(styles)}> ${text} </tr-text> `}
      </tr-flex>
    `;
  }

  clearUserSelection() {
    this._selectedUsers.next(emptyUserSelection);
  }

  protected _renderActionsDropdownBySelection(ref: Ref<XDropdown>) {
    const paymentPlugin = getPluginForScope('payment', this.product.value!.institutionID);
    // TODO this is super ugly (create new participation-overview plugin for this)
    const ikhPaymentActive = paymentPlugin.id === 'payment::ikh';
    const sdPaymentActive = paymentPlugin.id === 'payment::sd';

    const selectedItems = this._selectedUserData.value ?? [];

    const participationOverviewPlugin = getPluginForScope('participation-overview', this.product.value!.institutionID);
    const actions = () =>
      participationOverviewPlugin.getDropdownItemActions(this, selectedItems, ikhPaymentActive || sdPaymentActive);

    return html`
      <bh-dropdown-action-content
        slot="dropdown-content"
        heading=${this._translation.translate('PARTICIPATION_DATAVIEW', 'modifyParticipationStatus')}
        .actions=${actions}
        .dialogRef=${ref}
      ></bh-dropdown-action-content>
    `;
  }

  protected getFullOptionsFrom(
    product: Product,
    targetView: View<typeof ParticipationDataview>,
    coloring: ColorType,
    participationState: ParticipationState,
  ) {
    const paymentPlugin = getPluginForScope('payment', product.institutionID);
    const ikhPaymentActive = paymentPlugin.id === 'payment::ikh';
    const sdPaymentActive = paymentPlugin.id === 'payment::sd';
    const paymentActive = ikhPaymentActive || sdPaymentActive;

    const lecturerPlugin = getPluginForScope('participation-overview', product.institutionID);
    const cacheColumns = getCachedExtraColumns(product) ?? defaultExtraColumns;
    const extraColumns = cacheColumns.map(fieldName => ({ fieldName }));
    const customColumnsFromPlugin = lecturerPlugin.getColumnDefinitions(
      this,
      product,
      coloring,
      paymentActive,
      participationState,
      extraColumns,
    );

    const operation =
      product instanceof Einzelberatung
        ? 'subscribeParticipationEinzelberatungDataview_prop'
        : 'subscribeParticipationWorkshopDataview_prop';
    return buildFullOptions({
      class: ParticipationDataview,
      operation,
      emptyState: () =>
        dataviewEmptyState({
          styles: {
            background: coloring.shade(50).shade(100),
            gridColumn: '1 / span 3',
            color: coloring.shade(800),
            border: `${this.spacing.xxs} solid ${coloring.shade(200)}`,
            borderLeftWidth: '0',
            borderBottomWidth: '0',
            borderTopRightRadius: this.spacing.sm,
            marginTop: this.spacing.xs,
          },
          text: 'Niemand zugewiesen...',
        }),
      loader: () => dataviewLoadingState(this),
      view: targetView,
      isEntryClickable: false,
      selectionSet: {
        participation: {
          ikh_doNotSendInvoice: 1,
          appointmentParticipations: {
            _id: 1,
            appointmentID: 1,
          },
        },
      },
      // set customColumns using plugin
      customColumns: customColumnsFromPlugin,
      fieldSelectionOptions: {
        shouldIncludeField: (baseQLFieldDefinition, path, baseEntity) => {
          // only show show members that are relevant for the given institution
          if (baseEntity._class === 'UserStammdaten') {
            if (
              baseQLFieldDefinition.name === '_id' ||
              baseQLFieldDefinition.name === '_class' ||
              baseQLFieldDefinition.name === 'user' ||
              baseQLFieldDefinition.name === 'userID'
            )
              return false;
            const institutionStammdatenFields = UserStammdaten.getStammdatenFieldsForInstitution(product.institutionID);
            return institutionStammdatenFields.includes(baseQLFieldDefinition.name);
          }

          const whitelist = this.getFieldWhitelist(product);
          return (
            whitelist.includes(path.join('.') + '.' + baseQLFieldDefinition.name) ||
            whitelist.includes(baseQLFieldDefinition.name)
          );
        },
      },
    } satisfies DataViewOptions<typeof ParticipationDataview>);
  }

  // eslint-disable-next-line max-lines-per-function
  override render() {
    if (!this.product.value) return nothing;
    const product = this.product.value;
    const stateFilters = this._stateViews.value ?? {};
    const selections = this._selectedUserData.value ?? [];
    const stateStats = this._stateStats.value ?? {};

    const drawer = createRef<any>();

    const editFields = async () => {
      // @ToDo: diesen Key gibt es nicht!
      const stateKey = ParticipationState.IN_BY_ADMIN;
      const targetStatusColor = getParticipationStateColors(stateKey, this);
      const stateView = stateFilters[stateKey];
      const dataViewOptions = this.getFullOptionsFrom(product, stateView, targetStatusColor, stateKey).dataViewOptions;

      const options = dataViewOptions;
      const newSelection = await drawer.value?.chooseFields({
        ...options.fieldSelectionOptions,
        selected: options.view?.configuredColumns ?? [],
      });

      if (newSelection) {
        const preExistingFields = dataViewOptions.customColumns.map(col => col.fieldName).filter(isDefined);
        const cachedExtraFields = getCachedExtraColumns(product) ?? defaultExtraColumns;
        const selectionFields = newSelection.map(col => col.fieldName).filter(isDefined);
        const newFields = selectionFields.filter(it => !preExistingFields.includes(it));
        const fieldsToBeRemoved = preExistingFields.filter(it => !selectionFields.includes(it));
        const newCachedExtraFields = cachedExtraFields.filter(it => !fieldsToBeRemoved.includes(it));
        for (const field of newFields) newCachedExtraFields.push(field);

        setCachedExtraColumns(product, newCachedExtraFields);
        this.updateViews.next();
      }
    };

    return html`
      <x-field-selection-drawer ${ref(drawer)}></x-field-selection-drawer>

      <x-flex
        space="md"
        ${css({
          position: 'relative',
        })}
      >
        ${this.mode !== 'lecturer'
          ? html`
              <x-flex space-between space="md" wrap horizontal crossaxis-center>
                <x-h3>
                  ${this._translation.translate('PARTICIPATION_DATAVIEW', 'registerTo')} <b> ${product.name} </b>
                </x-h3>

                <tr-button-small icon="edit" @click=${editFields}>
                  <x-text> Felder bearbeiten </x-text>
                </tr-button-small>
              </x-flex>
            `
          : nothing}
        ${selections.length >= 1
          ? html`
              <x-flex
                horizontal
                space-between
                ${acss({
                  position: 'sticky',
                  top: `${this.spacing.xl + this.spacing.md}px`,
                  padding: this.spacing.xs,
                  border: `2px solid ${this.colors.secondary.shade(300)}`,
                  borderRadius: this.spacing.sm,
                  background: this.colors.tone.snowWhite!.opacity(0.92),
                  margin: `0 -${this.spacing.xs + 2}px`,
                  zIndex: '9',

                  'tr-icon': {
                    marginLeft: `-${this.spacing.xxs}`,
                  },
                  '[info]': {
                    color: this.colors.secondary,
                  },
                })}
              >
                <x-flex horizontal start crossaxis-center space="xs">
                  <tr-button-small icon="person" .color=${this.colors.secondary.shade(300)} .action=${async () => null}>
                    <tr-text bold ${css({ marginLeft: `-${this.spacing.xs}` })}>${selections.length}x</tr-text>
                  </tr-button-small>

                  <tr-text bold info> markierte Anmeldungen </tr-text>
                </x-flex>

                <x-flex horizontal start crossaxis-center space="xs">
                  ${this._currentUser.value?.hasPermission(Permission.MANAGE_PARTICIPATION)
                    ? html`
                        <tr-button-small
                          icon="mail"
                          .color=${this.colors.secondary}
                          .action=${async () => {
                            const mailAddresses = await getByID(
                              User,
                              this._selectedUserData.value.map(x => x.buyer?._id),
                            )({ email: 1 }).then(users => users.map(user => user.email));
                            if (mailAddresses.length === 10000) {
                              TrDialog.alert('Zu viele Nutzer gleichzeitig selektiert.');
                              throw new Error('Cannot select more than 10000 users at the same time');
                            }

                            const success = await BHDialogFreeFormMails.showPopup({
                              props: {
                                institutionID: product.institutionID,
                                mailAddresses,
                                mailTemplateType: MailTemplateType.FREE_FORM,
                                mailTemplateConstructor: (doc: PartialEntityData<InstitutionMailTemplate>) =>
                                  new InstitutionMailTemplate({ ...doc, institutionID: product.institutionID }),
                                mailSendOperation: sendFreeFormMail,
                              },
                            });

                            if (success) this._selectedUsers.next(emptyUserSelection);
                          }}
                        >
                          <x-text> Mail senden </x-text>
                        </tr-button-small>

                        <!--- NEXT-STATE DROPDOWN --->
                        <x-dropdown ${ref(this._selectedMoveDropdown)}>
                          <tr-button-small
                            .color=${this.colors.secondary}
                            icon="recenter"
                            @click=${() => this._selectedMoveDropdown.value?.toggle()}
                          >
                            <x-text> Verschieben in </x-text>
                          </tr-button-small>
                          <bh-participation-dataview-move-dropdown-content
                            slot="dropdown-content"
                            @move=${async (e: CustomEvent<{ stateKey: ParticipationState; stateTitle: string }>) => {
                              const stateKey = e.detail.stateKey;
                              const stateTitle = e.detail.stateTitle;

                              const updateRes = await XDialog.waitFor(
                                updateParticipationsStateAsAdminUser(
                                  this._selectedUserData.value?.map(userData => userData.participation._id) ?? [],
                                  stateKey,
                                )(),
                              );
                              if (updateRes) {
                                XSnackbar.show(
                                  `${
                                    this._selectedUserData.value?.length ?? ''
                                  } Anmeldungen wurden erfolreich in ${stateTitle} verschoben`,
                                );
                                this._selectedUsers.next(emptyUserSelection);
                              } else
                                XSnackbar.show(
                                  `Leider konnten nicht alle Anmeldungen in ${stateTitle} verschoben werden!`,
                                );
                            }}
                          ></bh-participation-dataview-move-dropdown-content>
                        </x-dropdown>
                      `
                    : nothing}

                  <!--- ACTIONS DROPDOWN --->
                  <x-dropdown ${ref(this._selectedActionDropdown)}>
                    <tr-button-small
                      .color=${this.colors.secondary}
                      icon="electric_bolt"
                      .action=${async () => this._selectedActionDropdown.value?.toggle()}
                    >
                      <x-text> Aktionen </x-text>
                    </tr-button-small>

                    ${this._renderActionsDropdownBySelection(this._selectedActionDropdown)}
                  </x-dropdown>

                  <tr-button-small
                    .color=${this.colors.secondary.shade(300)}
                    icon="clear"
                    .action=${async () => this._selectedUsers.next(emptyUserSelection)}
                  ></tr-button-small>
                </x-flex>
              </x-flex>
            `
          : nothing}
        ${repeat(Object.entries(stateFilters), ([key, stateView]) => {
          const stateKey = key as ParticipationState;
          const targetStatusColor = getParticipationStateColors(stateKey, this);
          const stateStat = stateStats[stateKey] ?? '?';

          //TODO: this is the relevant element
          return html`
            <x-drag-drop
              dropzone-accept-selector="[data-is-dragging]"
              @dropped=${(evt: ValueEvent<HTMLElement>) => {
                if (this._showTableDropAreas.value) this._showTableDropAreas.next(false);
              }}
            >
              ${guard(
                [stateView, stateStat],
                () => html`
                  <tr-data-view
                    .options=${this.getFullOptionsFrom(product, stateView, targetStatusColor, stateKey).dataViewOptions}
                    .coloring=${targetStatusColor}
                    .bordered=${true}
                    .expandable=${true}
                    .dragState=${this._showTableDropAreas.value}
                    ${css({ width: '100%' })}
                  >
                    <tr-flex horizontal space="xxs" crossaxis-center ${css({ marginLeft: this.spacing.xs })}>
                      <tr-h4 bold> ${this._translation.translate('WORKSHOPS', 'STATES', stateKey)} </tr-h4>
                      <tr-h4> (${stateStat}) </tr-h4>
                    </tr-flex>
                  </tr-data-view>
                `,
              )}
            </x-drag-drop>
          `;
        })}
      </x-flex>
    `;
  }
  private getFieldWhitelist(product: Product) {
    const participationFields = [
      'createdAt',
      'updatedAt',

      'isNoShow',
      'productPriceForUser',
      'fulfilledParticipationRequirements',
      'registrationDate',
      'signoffDate',
      'registrationDateThroughAdmin',
      'signoffDateThroughAdmin',
      'waitingListPlace',
      'nachrueckPriority',
      'nachrueckPossible',
    ];

    const buyerFields = [
      'firstName', //
      'lastName',
      'createdAt',
      'email',
      'stammdaten',
    ];

    const paymentFields = [
      'orderNr', //
      'orderNr.year',
      'orderNr.nr',
      'discount',
      'createdAt',
      'tax',
      'amount',
    ];

    const fields = [
      'buyer',
      ...buyerFields.map(it => `buyer.${it}`),

      'payment',
      ...paymentFields.map(it => `payment.${it}`),

      'participation',
      ...participationFields.map(it => `participation.${it}`),
    ];

    if (product instanceof Einzelberatung)
      fields.push(
        'berater', //
        ...buyerFields.map(it => `berater.${it}`),
      );

    return fields;
  }
}

@customElement('bh-participation-dataview-move-dropdown-content')
export class BhParticipationDataviewMoveDropdownContent extends ChemistryLitElement {
  @state() private readonly _translation = new TranslationController<BHTranslationDictionary>(this);

  override render() {
    return html`
      <x-card>
        <x-text> ${this._translation.translate('PARTICIPATION_DATAVIEW', 'modifyParticipationStatus')} </x-text>
        <x-flex
          ${css({
            height: '1px',
            width: '100%',
            background: this.colors.secondary.shade(100),
          })}
        ></x-flex>

        <x-flex ${css({ marginLeft: `-${this.spacing.md}`, marginRight: `-${this.spacing.md}` })}>
          ${repeat(Object.values(ParticipationState), stateKey => {
            const stateTitle = this._translation.translate('WORKSHOPS', 'STATES', stateKey);

            return html`
              <x-flex
                horizontal
                padding="xs"
                crossaxis-center
                space="xs"
                ${acss({
                  cursor: 'pointer',
                  padding: `${this.spacing.xs} ${this.spacing.md}`,
                  '&:hover': { background: this.colors.neutral.shade(100) },
                })}
                @click=${() => this.dispatchEvent(new CustomEvent('move', { detail: { stateKey, stateTitle } }))}
              >
                <x-flex
                  flex-none
                  ${css({
                    alignSelf: 'stretch',
                    background: getParticipationStateColors(stateKey, this),
                    width: `${this.spacing.xxs + 2}px`,
                    borderRadius: '3px',
                  })}
                ></x-flex>
                <tr-text
                  ${css({
                    lineHeight: `${this.spacing.md + this.spacing.xxs}px`,
                    paddingTop: '2px',
                  })}
                >
                  ${stateTitle}
                </tr-text>
              </x-flex>
            `;
          })}
        </x-flex>
      </x-card>
    `;
  }
}
